import type { LocationQuery } from 'vue-router';

import { LoginFlowType } from '~/graphql/generated/graphql';

/** A credential the server accepted, and what it said comes next. */
export interface SignInContinuation {
  flow: LoginFlowType;
  hasMatchedInvite: boolean;
  /** Handed back by a password reset, for the new password once signed in. */
  passwordChangeToken?: string | null;
  /** False when a matched invite adopted an account the email already held. */
  accountCreated?: boolean;
}

export type CompletedFlowResult = { flow: LoginFlowType; error: null } | { flow: null; error: string };

export const STEP_ENTER_PASSWORD = 'enter-password';
export const STEP_CHOOSE_ACCOUNT = 'choose-account';

/** The query key another page opens the login with, on a step its own sign-in stopped at. */
export const LOGIN_RESUME_QUERY_KEY = 'resume';

/** Steps a sign-in parked on the server can resume at, once the page that started it hands over. */
export const RESUMABLE_LOGIN_STEPS = [STEP_ENTER_PASSWORD, STEP_CHOOSE_ACCOUNT] as const;
export type ResumableLoginStep = typeof RESUMABLE_LOGIN_STEPS[number];

export const isResumableLoginStep = (value: unknown): value is ResumableLoginStep =>
  (RESUMABLE_LOGIN_STEPS as readonly unknown[]).includes(value);

const LOGIN_RESUME_STEPS: Partial<Record<LoginFlowType, ResumableLoginStep>> = {
  [LoginFlowType.PasswordRequired]: STEP_ENTER_PASSWORD,
  [LoginFlowType.AccountChoiceRequired]: STEP_CHOOSE_ACCOUNT,
};

/** The step the login page picks up a sign-in the server parked after this flow; null when it parked nothing. */
export const loginResumeStepFor = (flow: LoginFlowType): ResumableLoginStep | null => LOGIN_RESUME_STEPS[flow] ?? null;

/** The query that opens the login on the step a parked sign-in waits at. */
export const loginResumeQuery = ({ email, step }: { email: string; step: ResumableLoginStep }) => ({ email, [LOGIN_RESUME_QUERY_KEY]: step });

export const withoutLoginResume = (query: LocationQuery): LocationQuery => {
  const { email: _email, [LOGIN_RESUME_QUERY_KEY]: _resume, ...rest } = query;
  return rest;
};
